import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { InputSwitch } from 'primereact/inputswitch';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import { AddDispute } from '../../features/disputes/addDispute/AddDispute';
import { CloseAllDisputes } from '../../features/disputes/closeAllDisputes/CloseAllDisputes';
import { EditDispute } from '../../features/disputes/editDispute/EditDispute';
import ReviewDispute from '../../features/disputes/reviewDispute/ReviewDispute';
import { useGetPrimaryApprovalWindowQuery } from '../../pages/compensation/compensationSlice';
import {
    useGetAgentApprovalStatusSearchQuery,
    useGetDailyMetricsTableDetailsQuery,
    // useGetMonthlyMetricsSummaryQuery,
    useUpdateReviewStatusMutation,
} from '../../pages/metricdashboard/metricDashboardSlice';
import { formatDateISO8601 } from '../../utils/dateFormat';
import { ConstApprovalWindowStatus, ConstReviewStatus } from '../../utils/typeConstants';
import { DateTemplate } from '../dateTemplate/DateTemplate';
import {
    exportExcel,
    getClosedDisputesIds,
    hasResolvedDisputes,
    isRowSelectable,
    statusIcon,
} from './my-daily-metrics-table-service';
import './myDailyMetricsTable.scss';
import '../../styles/theme.scss';


type dailyMetricsProps = {
    employeenumber?: number;
    scorecardVersionId?: number;
    year?: number;
    month?: number;
    // data: any;
    monthlyMetricsData?: any;
};

// eslint-disable-next-line max-statements
export const MyDailyMetricsTable = ({
    employeenumber,
    scorecardVersionId,
    year,
    month,
    // data,
    monthlyMetricsData,
}: dailyMetricsProps) => {
    const [metricColHeaders, setMetricColHeaders] = useState<any>([]);
    const [metricColHeaderNames, setMetricColHeaderNames] = useState<any>([]);
    const [agents, setAgents] = useState<any>(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filterColumns, setFilterColumns] = useState<any>([]);
    const [selectedDailyMetric, setSelectedDailyMetric] = useState([]);
    const [disputeObject, setDisputeObject] = useState<any>([]);
    const [disputeIds, setDisputeIds] = useState<string[]>([]);
    const [closedDisputeIds, setClosedDisputeIds] = useState<string[]>([]);
    const [seletedRowData, setSeletedRowData] = useState<any>();
    const [UpdateReviewStatus] = useUpdateReviewStatusMutation();
    const [needReviewEmpDailyResultTd, setNeedReviewEmpDailyResultTd] = useState<number>(0);
    const [disputeDialogEnable, setDisputeDialogEnable] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);
    const [visibleEditDispute, setVisibleEditDispute] = useState<boolean>(false);
    const [visibleReviewDispute, setVisibleReviewDispute] = useState<boolean>(false);
    const [selectedMetric, setSelectedMetric] = useState<any>([]);
    const [empDailyResultIds, setempDailyResultIds] = useState<any>([]);
    const [disputedEmpDailyResultIds, setDisputedEmpDailyResultIds] = useState<any>([]);
    const [empDailyIdsandStatus, setEmpDailyIdsandStatus] = useState<any>([]);
    const menu = useRef<Menu>(null);
    const [hideEditMenu, setHideEditMenu] = useState(true);
    const [hideMarkAsNeedReviewMenu, setHideMarkAsNeedReviewMenu] = useState(false);
    const [hideViewDisputesMenu, setHideViewDisputesMenu] = useState(false);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    const [isMangerWindowOpen, setIsMangerWindowOpen] = useState<boolean>(false);
    const [showNonPayoutMetrics, setShowNonPayoutMetrics] = useState<boolean>(false); // OFF by default

    const showSeparator = () => {
        return !!hideViewDisputesMenu && (!!hideMarkAsNeedReviewMenu || !!hideEditMenu);
    };

    const payoutLabels = useMemo(() => {
        if (showNonPayoutMetrics) return null; // null = show all
        return new Set(
            (monthlyMetricsData?.data ?? [])
                .filter((m: any) => m.is_payout_metric)
                .map((m: any) => m.label)
        );
    }, [showNonPayoutMetrics, monthlyMetricsData]);

    const visibleColIndexes = useMemo(() => {
        if (!metricColHeaders || !payoutLabels) return metricColHeaders?.map((_: any, i: number) => i);
        return metricColHeaders
            .map((_: any, i: number) => i)
            .filter((i: number) => payoutLabels.has(metricColHeaders[i]));
    }, [metricColHeaders, payoutLabels]);

    // Calculate non-payout count
    const nonPayoutCount = useMemo(() => {
        return monthlyMetricsData?.data?.filter((m: any) => m.is_payout_metric === false).length ?? 0;
    }, [monthlyMetricsData]);

    const shouldShowToggle = nonPayoutCount > 0;

    const items = [];
    items.push(
        {
            label: 'View Disputes',
            icon: 'pi pi-eye',
            visible: !!hideViewDisputesMenu,
            command: () => {
                onViewDisputesClicked();
            },
        },
        { separator: showSeparator(), visible: showSeparator() },
        {
            label: 'Edit',
            icon: 'pi pi-pencil',
            visible: !!hideEditMenu,
            command: () => {
                setVisibleEditDispute(true);
            },
        },
        {
            label: 'Mark as needs review',
            icon: 'pi pi-check-circle',
            visible: !!hideMarkAsNeedReviewMenu,
            command: () => {
                onMarkAsNeedsReviewCliked();
            },
        }
    );

    const { data: dailyMetricsTableData, isFetching: isDailyMetricsTableFetching } = useGetDailyMetricsTableDetailsQuery(
        {
            employeenumber,
            year,
            month,
            scorecardVersionId,
            isPayoutMetric: !showNonPayoutMetrics
        },
        { skip: !employeenumber || !scorecardVersionId }
    );

    const { data: primaryApprovalWindowData } = useGetPrimaryApprovalWindowQuery(
        {
            year,
            month,
            scorecardVersionId,
            approvalWindowConfigLevelId: 3,
        },
        { skip: !year || !month || !scorecardVersionId }
    );

    const { data: agentApprovalStatusData } = useGetAgentApprovalStatusSearchQuery(
        {
            employeenumber,
            year,
            month,
            scorecardVersionId,
        },
        { skip: !employeenumber || !year || !month || !scorecardVersionId }
    );

    // const { data: monthlyMetricsData } = useGetMonthlyMetricsSummaryQuery(
    //     {
    //        employeenumber,
    //         year,
    //         month,
    //         scorecardVersionId,
    //         scorecardRoleId: 1,
    //         isPayoutMetric: hideNonPFPMetrics
    //     },
    //     { skip: !employeenumber || !year || !month || !scorecardVersionId }
    // );

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;
        const afilters = { ...filters };
        afilters.global.value = value;
        setFilters(afilters);
        setGlobalFilterValue(value);
    };

    const actionTemplate = (rowData: any) => {
        if (
            parseInt(rowData.review_status_id, 10) === ConstReviewStatus.NEEDS_REVIEW &&
            hasResolvedDisputes(rowData) === false
        ) {
            return <div />;
        }
        return (
            <Button
                type="button"
                icon="pi pi-ellipsis-v"
                onClick={(e: any) => {
                    popupMenuClickHandler(e, rowData);
                }}
            />
        );
    };

    const onResolveAllDisputesClicked = () => {
        const disputedEmpDailyResultIds = empDailyIdsandStatus
            .filter(
                (x: { reviewStatus: number; empDailyResultId: number }) =>
                    x.reviewStatus === ConstReviewStatus.DISPUTED
            )
            .map((y: { empDailyResultId: number }) => y.empDailyResultId);
        if (disputedEmpDailyResultIds.length > 0) {
            setDisputedEmpDailyResultIds(disputedEmpDailyResultIds);
            setDisputeDialogEnable(true);
        } else {
            setDisputeDialogEnable(false);
            setDisputedEmpDailyResultIds([]);
            toast.warn('Please select only Disputed Rows..!');
        }
    };
    const closeAllDisputes = (message: boolean) => {
        setDisputeDialogEnable(message);
        setVisible(message);
        setVisibleEditDispute(message);
        setDisputeObject([]);
        setDisputedEmpDailyResultIds([]);
        uncheckCheckboxesByClassName('tableCheckbox');
        setSelectedDailyMetric([]);
    };

    useEffect(() => {
        uncheckCheckboxesByClassName('tableCheckbox');
    }, []);

    const uncheckCheckboxesByClassName = (className: string) => {
        const checkboxes = document.getElementsByClassName(className) as HTMLCollectionOf<HTMLInputElement>;
        Array.from(checkboxes).forEach((checkbox) => {
            // eslint-disable-next-line no-param-reassign
            checkbox.checked = false;
        });
    };

    const renderHeader = () => {
        return (
            <div className="flex justify-content-between exportexcel">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={onGlobalFilterChange}
                        placeholder="Keyword Search"
                        aria-label="Global Search"
                    />
                </span>
                <div className="float-end">
                    <Button
                        disabled={empDailyResultIds.length === 0}
                        type="button"
                        onClick={onResolveAllDisputesClicked}
                        className="btn-primary"
                        label="Resolve All Disputes"
                        icon="pi pi-check"
                    />
                    <CloseAllDisputes
                        empDailyResultIds={disputedEmpDailyResultIds}
                        disputeDialogEnable={disputeDialogEnable}
                        closeAllDisputes={closeAllDisputes}
                        employeenumber={employeenumber}
                        year={year}
                        month={month}
                        scorecardVersionId={scorecardVersionId}
                    />
                    <Button
                        type="button"
                        style={{ marginLeft: 15 }}
                        icon="pi pi-upload me-2"
                        className="btn-export"
                        onClick={() => exportExcel(agents, metricColHeaders)}
                        data-pr-tooltip="XLS"
                    >
                        Export
                    </Button>
                </div>
            </div>
        );
    };

    const header = renderHeader();

    const onDisputeClicked = (rowData: any) => {
        if (checkAssociateWindowStatus()) {
            if (isMangerWindowOpen) {
                toast.warn(
                    'Sorry, you cannot create a dispute once the associate approval window has closed.'
                );
                return;
            }
            if (disputeObject.length > 0) {
                const date = formatDateISO8601(rowData.metric_date);
                const selectedMetricForDay = disputeObject.filter((x: any) => {
                    if (x.item.indexOf(date) > -1) return x;
                    else return '';
                });
                setSelectedMetric(selectedMetricForDay);
                setVisible(true);
            } else {
                toast.warn('Please select metric(s)');
            }
        }
    };

    useEffect(() => {
        if (primaryApprovalWindowData?.data?.length > 0) {
            const approvalWindowStatusId = primaryApprovalWindowData?.data[0]?.approval_window_status_id;
            if (
                [
                    ConstApprovalWindowStatus.APPROVALS_NOT_STARTED,
                    ConstApprovalWindowStatus.ASSOCIATE_WINDOW,
                ].includes(approvalWindowStatusId)
            ) {
                setIsMangerWindowOpen(false);
            } else {
                setIsMangerWindowOpen(true);
            }
        } else {
            setIsMangerWindowOpen(false);
        }
    }, [scorecardVersionId, year, month, primaryApprovalWindowData]);

    const onMarkAsNeedsReviewCliked = () => {
        const markAsReviewStatus = {
            empDailyResultIds: [needReviewEmpDailyResultTd],
            status: 1,
            reviewBy: employeenumber,
        };
        if (checkAssociateWindowStatus()) {
            updateReviewStatus(markAsReviewStatus);
        }
    };

    const onViewDisputesClicked = () => {
        setVisibleReviewDispute(true);
    };

    const checkAssociateWindowStatus = () => {
        if (
            (agentApprovalStatusData?.data[0]?.approval_window_status?.toLowerCase() ===
                'approvals not started' ||
                agentApprovalStatusData?.data[0]?.approval_window_status?.toLowerCase() ===
                'associate window' ||
                agentApprovalStatusData?.data[0]?.approval_window_status?.toLowerCase() ===
                'approvals halted') &&
            (agentApprovalStatusData?.data[0]?.agent_status_name?.toLowerCase() === 'unapproved' ||
                agentApprovalStatusData?.data[0]?.agent_status_name === null)
        ) {
            return true;
        } else {
            toast.warn(
                'Sorry, metric reviews/create a dispute are not allowed after the Agent Approval Window has closed.'
            );
            return false;
        }
    };

    const onReviewClicked = (rowData: any) => {
        const reviewedStatus = {
            empDailyResultIds: [rowData.emp_daily_result_id],
            status: 2,
            reviewBy: employeenumber,
        };
        if (checkAssociateWindowStatus()) {
            updateReviewStatus(reviewedStatus);
        }
    };

    const onReviewAllClicked = () => {
        if (checkAssociateWindowStatus()) {
            const empIDStatus = empDailyIdsandStatus
                .filter(
                    (x: { reviewStatus: number; empDailyResultId: number }) =>
                        x.reviewStatus === ConstReviewStatus.NEEDS_REVIEW
                )
                .map((y: { empDailyResultId: number }) => y.empDailyResultId);
            if (empIDStatus.length > 0) {
                const reviewedStatus = {
                    empDailyResultIds: empIDStatus,
                    status: 2,
                    reviewBy: employeenumber,
                };
                updateReviewStatus(reviewedStatus);
            } else {
                toast.warn('Please select only Need Review');
            }
        }
    };

    const updateReviewStatus = (reviewStatus: any) => {
        UpdateReviewStatus(reviewStatus)
            .unwrap()
            .then(() => {
                setNeedReviewEmpDailyResultTd(0);
                setSelectedDailyMetric([]);
                toast.info('Review-Status Update Success');
            })
            .then((error: any) => {
                if (error) console.log(error);
            });
    };

    const onMetricValueCheck = (e: any) => {
        let disputeMetric = {};
        disputeMetric = { item: e.target.name };
        const i = disputeObject.findIndex((x: any) => x.item === e.target.name);
        if (i === -1 && e.target.checked) {
            setDisputeObject([...disputeObject, disputeMetric]); // Add
        } else if (e.target.checked === false && i > -1) {
            const item = e.target.name;
            setDisputeObject(disputeObject.filter((y: any) => y.item !== item)); //remove
        }
    };

    const disputeStatusIcon = (rowData: any) => {
        const icon = statusIcon(rowData);
        const alt = rowData.review_status_name;
        return <div>{icon ? <img className="justify-content-left " src={icon} alt={alt} /> : ''}</div>;
    };

    const disputeReviewEditor = (rowData: any) => {
        if (rowData.review_status_id === ConstReviewStatus.NEEDS_REVIEW) {
            return (
                <div>
                    <Button
                        type="button"
                        onClick={() => onDisputeClicked(rowData)}
                        className="btn-secondary btn-sm"
                    >
                        Dispute
                    </Button>
                    <Button
                        type="button"
                        className="btn-primary btn-sm"
                        onClick={() => onReviewClicked(rowData)}
                    >
                        Review
                    </Button>
                </div>
            );
        } else return '';
    };

    const textEditor = (options: any, head: string, colHeader: any, disputeColNo: any, label: any) => {
        if (options.review_status_id === ConstReviewStatus.NEEDS_REVIEW) {
            const date = formatDateISO8601(options.metric_date);
            return (
                <div className="flex align-items-center">
                    <input
                        type="checkbox"
                        className="tableCheckbox me-1"
                        name={`${options.emp_daily_result_id}$$${colHeader}$$${date}$$${head}$$${label}`}
                        onClick={onMetricValueCheck}
                        value={head}
                    />
                    <label className="ml-2">{head}</label>
                </div>
            );
        } else if (options.review_status_id === 3) {
            const distArr = options.colDisputes.split('#');
            return (
                <label
                    className="ml-2"
                    style={{
                        color: distArr[disputeColNo]?.indexOf('NotDisputed') === -1 ? '#E23636' : '',
                    }}
                >
                    {head}
                </label>
            );
        } else {
            return <label className="ml-2">{head}</label>;
        }
    };

    const onSelectionChange = (event: any) => {
        setSelectedDailyMetric(event.value);
        const empDailyResultId = event.value.map((x: any) => x.emp_daily_result_id);
        const idnStatus = event.value.map((x: any) => {
            return { empDailyResultId: x.emp_daily_result_id, reviewStatus: x.review_status_id };
        });
        setempDailyResultIds(empDailyResultId);
        setEmpDailyIdsandStatus(idnStatus);
    };

    useEffect(() => {
        setAgents(dailyMetricsTableData?.data.length > 0 ? dailyMetricsTableData.data : []);
    }, [employeenumber, scorecardVersionId, year, month, dailyMetricsTableData, monthlyMetricsData]);

    // useEffect(() => {
    //     if (metricColHeaders != null) {
    //         setFilterColumns(['metric_date', 'col2', 'col3', 'col4', 'col5', 'col6', 'review_status_name']);

    //         const filters: any = {
    //             global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    //             reviewStatusName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    //         };
    //         ['metric_date', 'col2', 'col3', 'col4', 'col5', 'col6', 'review_status_name'].forEach(
    //             (ele: any) => {
    //                 const columnName = ele;
    //                 filters[columnName] = { value: null, matchMode: FilterMatchMode.CONTAINS };
    //             }
    //         );
    //         setFilters(filters);
    //     }
    // }, [metricColHeaders]);
    useEffect(() => {
        if (metricColHeaders != null) {
            const dynamicCols = metricColHeaders.map((_: any, index: number) => `col${index + 2}`);
            const allColumns = ['metric_date', ...dynamicCols, 'review_status_name'];

            setFilterColumns(allColumns);

            const filters: any = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                reviewStatusName: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
            };

            allColumns.forEach((columnName: string) => {
                filters[columnName] = { value: null, matchMode: FilterMatchMode.CONTAINS };
            });

            setFilters(filters);
        }
    }, [metricColHeaders]);

    useEffect(() => {
        // setMetricColHeaders(data?.data[0]?.colHeaders?.split('$', 5));
        // setMetricColHeaderNames(data?.data[0]?.colHeaderNames?.split('$', 5));
        setMetricColHeaders(dailyMetricsTableData?.data[0]?.colHeaders?.split('$'));
        setMetricColHeaderNames(dailyMetricsTableData?.data[0]?.colHeaderNames?.split('$'));
    }, [employeenumber, scorecardVersionId, year, month, dailyMetricsTableData, monthlyMetricsData, agents]);

    const popupMenuClickHandler = (e: any, rowData: any) => {
        e.preventDefault();
        if (rowData.review_status_id === ConstReviewStatus.DISPUTED) {
            setHideEditMenu(true);
            setHideMarkAsNeedReviewMenu(false);
            setHideViewDisputesMenu(false);
        } else {
            setHideEditMenu(false);
            if (rowData.review_status_id !== ConstReviewStatus.NEEDS_REVIEW) {
                setHideMarkAsNeedReviewMenu(true);
            } else {
                setHideMarkAsNeedReviewMenu(false);
            }
        }

        if (hasResolvedDisputes(rowData)) {
            setClosedDisputeIds(getClosedDisputesIds(rowData));
            setHideViewDisputesMenu(true);
        } else {
            setClosedDisputeIds([]);
            setHideViewDisputesMenu(false);
        }

        getDisputesIds(rowData);
        setSeletedRowData(rowData);
        setNeedReviewEmpDailyResultTd(rowData.emp_daily_result_id);

        menu.current?.toggle(e);
    };

    const getDisputesIds = (rowData: any) => {
        let disputeCol = rowData?.colDisputes?.split('#');
        disputeCol = disputeCol.filter((x: any) => x !== 'NotDisputed');
        setDisputeIds(disputeCol);
    };

    return (
        <div className="card datatable-doc-demo border-0 table">
            {shouldShowToggle && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 12px' }}>
                        <span style={{ color: '#6366f1', fontWeight: 600, fontSize: '14px' }}>Show Non-Payout Metrics</span>
                        <InputSwitch
                            checked={showNonPayoutMetrics}
                            onChange={(e) => setShowNonPayoutMetrics(e.value)}
                        />
                    </div>
                    <div style={{ fontStyle: 'italic', color: '#E23636', fontSize: '14px', padding: '6px 12px' }}>
                        *Data could be delayed up to 48 hours
                    </div>
                </div>
            )}
            <Menu model={items} popup ref={menu} id="popup_menu" className="headerFeatures" />
            {metricColHeaders != null ? (
                <DataTable
                    value={dailyMetricsTableData?.data}
                    header={header}
                    dataKey="emp_daily_result_id"
                    className="mainTable myDailyMetricTable mainTableFilterStyles"
                    loading={isDailyMetricsTableFetching}
                    filters={filters}
                    globalFilterFields={filterColumns}
                    filterDisplay="row"
                    selection={selectedDailyMetric}
                    onSelectionChange={onSelectionChange}
                    scrollable
                    scrollHeight="600px"
                    selectionMode="checkbox"
                    isDataSelectable={isRowSelectable}
                    tableStyle={{ minWidth: 'max-content' }}
                >
                    <Column
                        style={{ width: '3rem', minWidth: '3rem' }}
                        headerStyle={{ width: '3rem', minWidth: '3rem' }}
                        body={disputeStatusIcon}
                    />
                    <Column
                        selectionMode="multiple"
                        style={{ width: '3rem', minWidth: '3rem' }}
                        headerStyle={{ width: '3rem', minWidth: '3rem' }}
                    />
                    <Column
                        header="Date"
                        sortable
                        sortField="metric_date"
                        filterField="metric_date"
                        filterMenuStyle={{ width: '10rem' }}
                        style={{ width: '10rem', minWidth: '10rem' }}
                        headerStyle={{ width: '10rem', minWidth: '10rem' }}
                        field="metric_date"
                        filterPlaceholder={'Search'}
                        body={(date) => DateTemplate(date?.metric_date)}
                        filter
                    />
                    {visibleColIndexes?.map((index: number) => (
                        <Column
                            key={metricColHeaders[index]}
                            header={metricColHeaders[index]}
                            sortable
                            filterMenuStyle={{ width: '10rem' }}
                            style={{ width: '10rem', minWidth: '10rem' }}
                            headerStyle={{ width: '10rem', minWidth: '10rem' }}
                            filter
                            filterPlaceholder={'Search'}
                            body={(rowData) =>
                                textEditor(
                                    rowData,
                                    rowData.colValues?.[index],
                                    metricColHeaderNames[index],
                                    index,
                                    metricColHeaders[index]
                                )
                            }
                        />
                    ))}
                    <Column
                        header="Status"
                        sortable
                        sortField="review_status_name"
                        filterField="review_status_name"
                        filterMenuStyle={{ width: '10rem' }}
                        style={{ width: '10rem', minWidth: '10rem' }}
                        headerStyle={{ width: '10rem', minWidth: '10rem' }}
                        field="review_status_name"
                        filter
                        filterPlaceholder={'Search'}
                    />
                    <Column
                        style={{ width: '180px', minWidth: '180px', padding: 0, paddingBottom: 2, paddingTop: 2 }}
                        headerStyle={{ width: '180px', minWidth: '180px', padding: 0 }}
                        body={disputeReviewEditor}
                    />
                    <Column
                        body={actionTemplate}
                        style={{ width: '3rem', minWidth: '3rem', display: 'flex', justifyContent: 'flex-end' }}
                        headerStyle={{ width: '3rem', minWidth: '3rem' }}
                    />
                </DataTable>
            ) : (
                <DataTable
                    value={dailyMetricsTableData?.data}
                    header={header}
                    className="mainTable"
                    loading={isDailyMetricsTableFetching}
                    rows={5}
                    paginator={dailyMetricsTableData?.data?.length > 5}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    filters={filters}
                    globalFilterFields={filterColumns}
                    filterDisplay="row"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                />
            )}
            <div>
                <Button
                    disabled={empDailyResultIds.length === 0}
                    type="button"
                    className="btn-primary mt-2 float-end"
                    onClick={onReviewAllClicked}
                >
                    Review Selected
                </Button>
            </div>
            {visible ? (
                <AddDispute
                    disputeDialogEnable={visible}
                    closeAllDisputes={closeAllDisputes}
                    selectedMetric={selectedMetric}
                    employeeNo={employeenumber ?? 0}
                />
            ) : (
                ''
            )}
            {visibleEditDispute ? (
                <EditDispute
                    disputeDialogEnable={visibleEditDispute}
                    closeAllDisputes={closeAllDisputes}
                    empDailyResultId={needReviewEmpDailyResultTd}
                    disputeIds={disputeIds}
                    employeeNo={employeenumber ?? 0}
                    // monthlyMetricsData={monthlyMetricsData}
                    seletedRowData={seletedRowData}
                    isMangerWindowOpen={isMangerWindowOpen}
                    setDisputeDialogEnable={setVisibleReviewDispute}
                    scorecardVersionId={scorecardVersionId}
                    year={year}
                    month={month}
                />
            ) : (
                ''
            )}
            {visibleReviewDispute ? (
                <ReviewDispute
                    disputeDialogEnable={visibleReviewDispute}
                    setDisputeDialogEnable={setVisibleReviewDispute}
                    disputesId={closedDisputeIds}
                    employeeNo={employeenumber ?? 0}
                    empDailyResultId={needReviewEmpDailyResultTd}
                    monthlyMetricsData={monthlyMetricsData?.data ?? []}
                />
            ) : (
                ''
            )}
        </div>
    );
};
